import React from "react";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import { usePhcTheme } from "../PhcThemeContext";
import ThemeToggle from "../ThemeToggle";

const META_STATIC = {
  reportType: "Attribution Analysis",
  generatedBy: "Jarvis AI v4.2",
};

const TRUST_STATS = [
  { value: "9+", label: "YEARS OF LIVE MODEL" },
  {
    value: "10k+",
    label: "INVESTORS PROFILING DONE",
    labelLines: ["INVESTORS PROFILING", "DONE"],
  },
  { value: "5", label: "RETAIL PRODUCTS" },
  { value: "2", label: "HNI OFFERINGS" },
];

// const JarvisLightLogo = () => (
//   <Box sx={{ display: "flex", flexDirection: "column", gap: "5.26px" }}>
//     <Box
//       sx={{
//         position: "relative",
//         width: 132.53,
//         height: 29.09,
//         flexShrink: 0,
//       }}
//     >
//       <Box
//         component="img"
//         src="/images/phc-dashboard/jarvis-blue-logo.svg"
//         alt="Jarvis"
//         sx={{
//           position: "absolute",
//           left: 0,
//           top: 0,
//           width: 125.8,
//           height: 29.09,
//           display: "block",
//         }}
//       />
//       <Box
//         component="img"
//         src="/images/phc-dashboard/jarvis-blue-logo-mark.svg"
//         alt=""
//         sx={{
//           position: "absolute",
//           left: 127.17,
//           top: 0.39,
//           width: 4.873,
//           height: 4.873,
//           display: "block",
//         }}
//       />
//     </Box>
//     <Text
//       sx={{
//         fontWeight: 400,
//         fontSize: 12.311,
//         lineHeight: 1,
//         color: "#000000",
//         letterSpacing: "-0.3693px",
//         whiteSpace: "nowrap",
//       }}
//     >
//       Investing powered by AI
//     </Text>
//   </Box>
// );

const DesktopHeaderSaveButton = ({ onSaveReport }) => {
  const { theme } = usePhcTheme();
  if (!onSaveReport) return null;

  return (
    <Box
      component="button"
      type="button"
      onClick={onSaveReport}
      sx={{
        width:"fit-content",
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "7.6px",
        px: "24px",
        py: "13px",
        borderRadius: "10px",
        border: `1px solid ${theme.sectionBorder}`,
        cursor: "pointer",
        backgroundColor: "#007EF0",
        boxShadow: theme.cardShadow,
        overflow: "hidden",
        "&:hover": { filter: "brightness(1.08)" },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: "inset 0px 0px 18px 0px rgba(0,126,240,0.2)",
        },
      }}
    >
      <Text
        sx={{
          position: "relative",
          zIndex: 1,
          fontWeight: 500,
          fontSize: 16,
          color: "#FFFFFF",
          lineHeight: "20px",
          letterSpacing: "0.48px",
          whiteSpace: "nowrap",
        }}
      >
        Save Report
      </Text>
      <Box
        component="img"
        src="/images/phc-dashboard/save-report-arrow.svg"
        alt=""
        sx={{
          position: "relative",
          zIndex: 1,
          width: 20,
          height: 20,
          display: "block",
          flexShrink: 0,
        }}
      />
    </Box>
  );
};

const MetaItemDesktop = ({ label, value }) => {
  const { theme } = usePhcTheme();

  return (
    <Stack spacing={0.75} alignItems="flex-end">
      <Text
        sx={{
          fontWeight: 500,
          fontSize: 16,
          color: theme.metaLabelColor,
          textTransform: "uppercase",
          letterSpacing: "0.02em",
        }}
      >
        {label}
      </Text>
      <Text
        sx={{
          fontWeight: 600,
          fontSize: 20,
          color: theme.textPrimary,
          textAlign: "right",
        }}
      >
        {value || "-"}
      </Text>
    </Stack>
  );
};

const MetaCellMobile = ({ label, value, align = "left", valueColor }) => {
  const { theme } = usePhcTheme();

  return (
    <Stack
      spacing="2px"
      alignItems={align === "right" ? "flex-end" : "flex-start"}
      sx={{ width: "100%" }}
    >
      <Text
        sx={{
          fontWeight: 400,
          fontSize: 10,
          color: theme.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          lineHeight: "15px",
          textAlign: align,
        }}
      >
        {label}
      </Text>
      <Text
        sx={{
          fontWeight: 400,
          fontSize: 14,
          color: valueColor || theme.textPrimary,
          lineHeight: "20px",
          textAlign: align,
          whiteSpace: "nowrap",
        }}
      >
        {value || "-"}
      </Text>
    </Stack>
  );
};

/**
 * Mobile primary Save CTA — Figma 5626:88625
 * Placed between metadata card and trust stats (Figma 5626:88598).
 */
export const MobileSaveReportButton = ({ onSaveReport }) => {
  if (!onSaveReport) return null;

  return (
    <Box
      component="button"
      type="button"
      onClick={onSaveReport}
      sx={{
        position: "relative",
        display: "flex",
        width: "100%",
        height: 42,
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        borderRadius: "12px",
        border: "0.56px solid #393939",
        cursor: "pointer",
        overflow: "hidden",
        background:
          "linear-gradient(-12.28deg, rgba(0, 126, 240, 0.5) 0%, rgba(2, 118, 222, 0.5) 100%)",
        backdropFilter: "blur(9px)",
        WebkitBackdropFilter: "blur(9px)",
        boxShadow: "0px 2.25px 7.38px 3.44px rgba(41,119,236,0.3)",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: "inset 0px 0px 14.5px 0px rgba(41,119,236,0.3)",
        },
        "&:hover": { filter: "brightness(1.08)" },
      }}
    >
      <Text
        sx={{
          position: "relative",
          zIndex: 1,
          fontWeight: 500,
          fontSize: 14,
          color: "#FFFFFF",
          lineHeight: 1,
        }}
      >
        Save Report
      </Text>
      <Box
        component="img"
        src="/images/phc-dashboard/save-report-arrow.svg"
        alt=""
        sx={{
          position: "relative",
          zIndex: 1,
          width: 20,
          height: 20,
          display: "block",
        }}
      />
    </Box>
  );
};

/**
 * Figma desktop 9876:23029 / light 9862:20952 + mobile 5626:88598
 * Logo is omitted; subtitle + Save Report show in Light and Dark.
 */
const PhcReportHeader = ({ reportDate, portfolioName, onSaveReport }) => {
  const { theme, mode } = usePhcTheme();
  const isLight = mode === "light";

  return (
    <Stack spacing={{ xs: "31px", md: 4 }} sx={{ width: "100%" }}>
      {/* —— MOBILE layout (Figma 5626:88598) —— */}
      <Stack
        spacing="31px"
        sx={{ display: { xs: "flex", md: "none" }, width: "100%" }}
      >
        <Stack spacing={1.5} sx={{ width: "100%" }}>
          <Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <ThemeToggle />
          </Stack>
          <Stack spacing={1} sx={{ width: "100%" }}>
            <Box sx={{ width: "100%" }}>
              <Text
                sx={{
                  fontWeight: 400,
                  fontSize: 28,
                  lineHeight: "37.5px",
                  color: isLight ? undefined : theme.mobileTitleColor,
                  background: isLight ? theme.titleGradient : undefined,
                  WebkitBackgroundClip: isLight ? "text" : undefined,
                  WebkitTextFillColor: isLight ? "transparent" : undefined,
                  backgroundClip: isLight ? "text" : undefined,
                  textShadow: isLight ? "none" : "0px 0px 10px rgba(0,133,255,0.3)",
                  display: "block",
                  width: "100%",
                }}
              >
                PHC Portfolio Analytics
              </Text>
              <Text
                sx={{
                  fontWeight: 400,
                  fontSize: 30,
                  lineHeight: "37.5px",
                  color: isLight ? undefined : theme.mobileTitleColor,
                  background: isLight ? theme.titleGradient : undefined,
                  WebkitBackgroundClip: isLight ? "text" : undefined,
                  WebkitTextFillColor: isLight ? "transparent" : undefined,
                  backgroundClip: isLight ? "text" : undefined,
                  textShadow: isLight ? "none" : "0px 0px 10px rgba(0,133,255,0.3)",
                  display: "block",
                  width: "100%",
                }}
              >
                Report
              </Text>
            </Box>
            <Text
              sx={{
                fontWeight: 400,
                fontSize: 14,
                color: theme.mobileSubtitleColor,
                lineHeight: "20px",
              }}
            >
              Your portfolio health check is complete.
            </Text>
          </Stack>
        </Stack>

        {/* 2×2 metadata card */}
        <Box
          sx={{
            width: "100%",
            borderRadius: "12px",
            border: theme.mobileMetaBorder,
            bgcolor: theme.mobileMetaBg,
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)",
            px: "17px",
            pt: "17.7px",
            pb: "17px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: "16px",
            rowGap: "16px",
          }}
        >
          <MetaCellMobile label="REPORT DATE" value={reportDate} />
          <MetaCellMobile
            label="PORTFOLIO"
            value={portfolioName}
            align="right"
          />
          <MetaCellMobile
            label="REPORT TYPE"
            value={META_STATIC.reportType}
          />
          <MetaCellMobile
            label="GENERATED BY"
            value={META_STATIC.generatedBy}
            align="right"
            valueColor="#2DD4BF"
          />
        </Box>

        <MobileSaveReportButton onSaveReport={onSaveReport} />

        {/* Trust stats 2×2 */}
        <Box
          sx={{
            width: "100%",
            borderRadius: "16px",
            border: theme.mobileMetaBorder,
            bgcolor: theme.mobileMetaBg,
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)",
            px: "21px",
            pt: "29.7px",
            pb: "21px",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              rowGap: 0,
              position: "relative",
            }}
          >
            {TRUST_STATS.map((stat, index) => {
              const isLeft = index % 2 === 0;
              const isTop = index < 2;
              return (
                <Box
                  key={stat.label}
                  sx={{
                    pt: isTop ? 0 : "24px",
                    pb: isTop ? "12.5px" : 0,
                    pr: isLeft ? "9px" : 0,
                    pl: isLeft ? 0 : "8px",
                    borderRight: isLeft ? theme.mobileMetaBorder : "none",
                    borderTop: !isTop ? theme.mobileMetaBorder : "none",
                  }}
                >
                  <Text
                    sx={{
                      fontWeight: 400,
                      fontSize: 20,
                      color: theme.textPrimary,
                      lineHeight: "28px",
                      mb: "4px",
                    }}
                  >
                    {stat.value}
                  </Text>
                  {stat.labelLines ? (
                    <Stack spacing={0}>
                      {stat.labelLines.map((line) => (
                        <Text
                          key={line}
                          sx={{
                            fontWeight: 400,
                            fontSize: 10,
                            color: theme.mobileSubtitleColor,
                            letterSpacing: "0.25px",
                            textTransform: "uppercase",
                            lineHeight: "12.5px",
                          }}
                        >
                          {line}
                        </Text>
                      ))}
                    </Stack>
                  ) : (
                    <Text
                      sx={{
                        fontWeight: 400,
                        fontSize: 10,
                        color: theme.mobileSubtitleColor,
                        letterSpacing: "0.25px",
                        textTransform: "uppercase",
                        lineHeight: "12.5px",
                      }}
                    >
                      {stat.label}
                    </Text>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Stack>

      {/* —— DESKTOP layout —— */}
      <Stack
        spacing={4}
        sx={{ display: { xs: "none", md: "flex" }, width: "100%" }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={4}
          sx={{ width: "100%" }}
        >
          <Stack spacing="28px" sx={{ maxWidth: 640, flex: 1 }}>
            <Stack spacing="6px" sx={{ width: "100%" }}>
              <Text
                sx={{
                  fontWeight: 600,
                  fontSize: 42,
                  lineHeight: 1.15,
                  background: theme.titleGradient,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                PHC Portfolio Analytics Report
              </Text>
              <Text
                sx={{
                  fontWeight: 400,
                  fontSize: 24,
                  color: theme.subtitleColor,
                  lineHeight: 1.3,
                }}
              >
                Your portfolio health check is complete.
              </Text>
            </Stack>
            <DesktopHeaderSaveButton onSaveReport={onSaveReport} />
          </Stack>

          <Stack spacing={1.25} sx={{ flexShrink: 0, width: 220, pt: 0.5 }} alignItems="flex-end">
            <ThemeToggle />
            <MetaItemDesktop label="Report Date" value={reportDate} />
            <MetaItemDesktop label="Portfolio" value={portfolioName} />
            <MetaItemDesktop
              label="Report Type"
              value={META_STATIC.reportType}
            />
            <MetaItemDesktop
              label="Generated By"
              value="Jarvis AI Engine v4.2"
            />
          </Stack>
        </Stack>

        <Box
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: "20px",
            border: `1px solid ${theme.sectionBorder}`,
            backgroundImage: theme.sectionBg,
            backgroundColor: theme.cardBg,
            boxShadow: theme.cardShadow,
            px: { md: 4, lg: 5 },
            py: { md: 3, lg: 3.5 },
            overflow: "hidden",
            "&::after": {
              content: isLight ? "none" : '""',
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              pointerEvents: "none",
              boxShadow: theme.sectionInsetGlow,
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              gap: isLight ? "52px" : 0,
            }}
          >
            {[
              { value: "9+", label: "Years of live model" },
              { value: "10,000+", label: "Investors Profiling Done" },
              { value: "5", label: "Retail Products" },
              { value: "2", label: "HNI Offerings" },
            ].map((stat, index) => (
              <React.Fragment key={stat.label}>
                {index > 0 && !isLight && (
                  <Box
                    sx={{
                      width: "1px",
                      height: 40,
                      bgcolor: theme.dividerColor,
                      flexShrink: 0,
                      alignSelf: "center",
                      mx: { md: 2, lg: 3 },
                    }}
                  />
                )}
                {index > 0 && isLight && (
                  <Box
                    sx={{
                      width: "1px",
                      height: 60,
                      bgcolor: theme.dividerColor,
                      flexShrink: 0,
                      alignSelf: "center",
                    }}
                  />
                )}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={isLight ? "4px" : 1.5}
                  sx={{
                    flexShrink: 0,
                    justifyContent: "center",
                    px: isLight ? 0 : { md: 2, lg: 3 },
                  }}
                >
                  <Text
                    sx={{
                      fontWeight: 700,
                      fontSize: isLight ? 28 : { md: 24, lg: 28 },
                      color: theme.textPrimary,
                      whiteSpace: "nowrap",
                      lineHeight: 1.1,
                    }}
                  >
                    {stat.value}
                  </Text>
                  <Text
                    sx={{
                      fontWeight: 400,
                      fontSize: isLight ? 18 : { md: 14, lg: 16 },
                      color: theme.textPrimary,
                      whiteSpace: "nowrap",
                      lineHeight: 1.25,
                    }}
                  >
                    {stat.label}
                  </Text>
                </Stack>
              </React.Fragment>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};

/**
 * Bottom Save Report bar — Light and Dark, at the end of the report.
 */
export const SaveReportBar = ({ onSaveReport }) => {
  const { theme } = usePhcTheme();

  if (!onSaveReport) return null;

  return (
    <Box
      sx={{
        display: "flex",
        position: "relative",
        zIndex: 1,
        width: "100%",
        minHeight: { xs: 72, md: 86 },
        alignItems: "center",
        justifyContent: "center",
        mt: 3,
        px: { xs: 0, md: "60px" },
        py: "13px",
        border: `1px solid ${theme.sectionBorder}`,
        borderRadius: 0,
        boxShadow: theme.saveBarShadow,
        backgroundColor: theme.saveBarBg,
        backgroundImage: theme.saveBarBgImage,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          boxShadow: theme.insetGlow,
        },
      }}
    >
      <Box
        sx={{
          zIndex: 1,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={onSaveReport}
          sx={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "7.6px",
            width: { xs: "100%", md: "175.6px" },
            minWidth: { xs: 0, md: "175.6px" },
            minHeight: 50,
            px: "24px",
            py: "13px",
            borderRadius: "10px",
            border: `1px solid ${theme.sectionBorder}`,
            cursor: "pointer",
            backgroundColor: theme.saveButtonBg === "#007EF0" ? "#007EF0" : "transparent",
            backgroundImage:
              theme.saveButtonBg === "#007EF0" ? "none" : theme.saveButtonBg,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            boxShadow: theme.cardShadow,
            overflow: "hidden",
            "&:hover": { filter: "brightness(1.1)" },
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              pointerEvents: "none",
              boxShadow: theme.sectionInsetGlow,
            },
          }}
        >
          <Text
            sx={{
              position: "relative",
              zIndex: 1,
              fontWeight: 500,
              fontSize: 18,
              color: theme.saveButtonText,
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            Save Report
          </Text>
          <Box
            component="img"
            src="/images/phc-dashboard/save-report-icon.svg"
            alt=""
            sx={{
              position: "relative",
              zIndex: 1,
              width: 24,
              height: 24,
              display: "block",
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PhcReportHeader;
